import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  InputAdornment,
  Menu,
} from '@mui/material';
import {
  AccountBalance as AccountIcon,
  CreditCard as LoanIcon,
  Home as MortgageIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as BestEverIcon,
  Note as NoteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { createToast, inferToastSeverityFromMessage, type AppToast } from '../utils/appToast';
import { APP_TOP_TOAST_SNACKBAR_SX } from '../utils/appSnackbarSx';
import { ToastCodeCaption } from './ToastCodeCaption';
import { useAuth } from '../contexts/AuthContext';
import { TABLE_HEADER_NEUTRAL_BG } from '../theme';
import { FIT_TABLE_SX, FIT_TABLE_CONTAINER_SX, FitColgroup, buildFitColgroup } from '../utils/fitTable';
import { filterBankAccountsForSeanViewer, isSeanViewerEmail } from '../utils/seanViewer';
import { roundToCents, vsLastEntryDifference } from '../utils/moneyCents';

interface ManualBalance {
  id: number;
  user_id: number;
  account_id?: number;
  loan_id?: number;
  balance: number;
  currency: string;
  notes?: string;
  date_entered: string;
  created_at: string;
  updated_at: string;
  account_name?: string;
  loan_name?: string;
}

interface Account {
  id: number;
  account_name: string;
  account_type: string;
}

interface Loan {
  id: number;
  loan_name: string;
  loan_type: string;
}

const ManualBalanceManagement: React.FC = () => {
  const { user } = useAuth();
  const userEmail = (user?.email || '').trim().toLowerCase();
  const isSeanManualView = isSeanViewerEmail(userEmail);
  const [balances, setBalances] = useState<ManualBalance[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const accounts = useMemo(
    () => filterBankAccountsForSeanViewer(allAccounts, userEmail),
    [allAccounts, userEmail],
  );
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [canManage, setCanManage] = useState<boolean>(false);
  const [toast, setToast] = useState<AppToast | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addEntityDialogOpen, setAddEntityDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportProgress, setBulkImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCell, setSelectedCell] = useState<{
    type: 'account' | 'loan';
    id: number;
    date: string;
    name: string;
  } | null>(null);
  const [isAddNewMode, setIsAddNewMode] = useState(false);
  const [formData, setFormData] = useState({
    date_entered: '',
    time_entered: '',
    balance: '',
    currency: 'EUR',
    notes: '',
    account_id: null as number | null,
    loan_id: null as number | null,
  });
  const [entityFormData, setEntityFormData] = useState({
    name: '',
    category: 'business_bank_account', // 'mortgage', 'business_loan', 'personal_loan', 'business_bank_account', 'personal_bank_account', 'other'
    account_number: '',
    bank_name: '',
    lender: '',
    original_amount: '',
    current_balance: '',
    interest_rate: '',
    monthly_payment: '',
  });

  const showToast = (message: string, severity?: AppToast['severity']) => {
    const resolvedSeverity = severity || inferToastSeverityFromMessage(message);
    setToast(createToast(resolvedSeverity, message));
  };

  const handleCloseToast = () => {
    setToast(null);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Use longer timeout for heavy parallel loads
      const timeoutConfig = { timeout: 90000 }; // 90 seconds
      
      // Fetch all data in parallel for faster page load
      // This allows all three API calls to run simultaneously instead of sequentially
      const [balancesRes, accountsRes, loansRes] = await Promise.allSettled([
        api.get('/api/manual-balances/', timeoutConfig),
        api.get('/api/accounts/', timeoutConfig),
        api.get('/api/loans/', timeoutConfig)
      ]);
      
      // Process results independently so one failure doesn't break everything
      const errors: string[] = [];
      
      // Process manual balances
      if (balancesRes.status === 'fulfilled') {
        try {
          setBalances(balancesRes.value.data.balances || []);
          setCanManage(balancesRes.value.data.can_manage || false);
        } catch (err: any) {
          console.error('Error processing manual balances:', err);
          errors.push(`Manual balances: ${err.message || 'Failed to process data'}`);
          setBalances([]);
          setCanManage(false);
        }
      } else {
        console.error('Error fetching manual balances:', balancesRes.reason);
        const err = balancesRes.reason;
        errors.push(`Manual balances: ${err.response?.data?.message || err.response?.data?.error || err.message || 'Request failed'}`);
        setBalances([]);
        setCanManage(false);
      }
      
      // Process accounts
      if (accountsRes.status === 'fulfilled') {
        try {
          setAllAccounts(accountsRes.value.data.accounts || []);
        } catch (err: any) {
          console.error('Error processing accounts:', err);
          errors.push(`Accounts: ${err.message || 'Failed to process data'}`);
          setAllAccounts([]);
        }
      } else {
        console.error('Error fetching accounts:', accountsRes.reason);
        const err = accountsRes.reason;
        errors.push(`Accounts: ${err.response?.data?.message || err.response?.data?.error || err.message || 'Request failed'}`);
        setAllAccounts([]);
      }
      
      // Process loans
      if (loansRes.status === 'fulfilled') {
        try {
          setLoans(loansRes.value.data.loans || []);
        } catch (err: any) {
          console.error('Error processing loans:', err);
          errors.push(`Loans: ${err.message || 'Failed to process data'}`);
          setLoans([]);
        }
      } else {
        console.error('Error fetching loans:', loansRes.reason);
        const err = loansRes.reason;
        errors.push(`Loans: ${err.response?.data?.message || err.response?.data?.error || err.message || 'Request failed'}`);
        setLoans([]);
      }
      
      // Show error if any requests failed
      if (errors.length > 0) {
        setError(`Some data failed to load: ${errors.join('; ')}`);
      }
    } catch (err: any) {
      console.error('Unexpected error fetching data:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load manual balance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper function to get week range (Sunday to Saturday) for a given date
  const getWeekRange = (date: Date): { start: Date; end: Date; startStr: string; endStr: string } => {
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - date.getDay()); // Get Sunday of the week
    
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6); // Get Saturday of the week
    
    return {
      start: sunday,
      end: saturday,
      startStr: sunday.toISOString().split('T')[0],
      endStr: saturday.toISOString().split('T')[0]
    };
  };

  // Helper function to get ISO week number
  const getISOWeekNumber = (date: Date): { year: number; week: number } => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { year: d.getUTCFullYear(), week: weekNo };
  };

  // Helper function to format date with ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Helper function to format week range for display
  const formatWeekRange = (startStr: string, endStr: string): string => {
    const start = new Date(startStr);
    const end = new Date(endStr);
    
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    const endDay = end.getDate();
    
    if (startMonth === endMonth) {
      return `${startDay}${getOrdinalSuffix(startDay)} - ${endDay}${getOrdinalSuffix(endDay)} ${startMonth}`;
    } else {
      return `${startDay}${getOrdinalSuffix(startDay)} ${startMonth} - ${endDay}${getOrdinalSuffix(endDay)} ${endMonth}`;
    }
  };

  // Helper function to format week column (Year-Week Number)
  const formatWeekColumn = (startStr: string, endStr: string): { yearWeek: string; dateRange: string } => {
    const start = new Date(startStr);
    const { year, week } = getISOWeekNumber(start);
    const yearWeek = `${year} - Wk ${week.toString().padStart(2, '0')}`;
    const dateRange = formatWeekRange(startStr, endStr);
    return { yearWeek, dateRange };
  };

  // Helper to format header labels with line breaks: "Rev Biz" / "Ire", "BOI" / "Mortgage", "Vs" / "last Entry"
  // Matches manual vs Revolut API suffixes (e.g. "Rev Biz Ire — manual", "Rev Biz Ire — Revolut API").
  const formatHeaderLabel = (name: string, type: 'account' | 'mortgage' | 'loan' | 'vs'): string[] => {
    if (type === 'account') {
      const n = name.trim();
      if (/rev biz/i.test(n) && /ire/i.test(n) && !/rev biz\s+uk/i.test(n)) {
        return ['Rev Biz', 'Ire'];
      }
      if (/rev biz/i.test(n) && /\buk\b/i.test(n)) {
        return ['Rev Biz', 'UK'];
      }
      return [name];
    }
    if (type === 'mortgage') {
      if (name.endsWith(' Mortgage')) return [name.replace(' Mortgage', ''), 'Mortgage'];
      return [name];
    }
    if (type === 'loan') {
      const n = name.trim();
      if (n.includes('Personal Loan')) {
        const namePart = n.replace(/\s*Personal\s+Loan\s*$/i, '').replace(/^Personal\s+Loan\s+from\s+/i, '').trim();
        return [namePart || n, 'Pers Loan'];
      }
      return [name];
    }
    if (type === 'vs') {
      const parts = name.split(' ');
      return [parts[0], parts.slice(1).join(' ')];
    }
    return [name];
  };

  const accountExportHeader = (account: Account): string =>
    `${account.account_name} (ID ${account.id})`;

  // Helper function to get all week ranges in the past (newest first)
  // Dynamically calculates based on oldest balance date or goes back 10 years
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getAllWeekRanges = useMemo(() => {
    const weekRanges = [];
    const today = new Date();
    
    // Find the oldest balance date if balances exist
    let oldestDate: Date | null = null;
    if (balances.length > 0) {
      const dates = balances.map(b => new Date(b.date_entered));
      oldestDate = new Date(Math.min(...dates.map(d => d.getTime())));
    }
    
    // Calculate how far back to go:
    // - If we have balances, go back to the oldest balance (with 1 week buffer)
    // - Otherwise, go back 10 years (520 weeks) as a safe default
    let weeksToGoBack = 520; // Default: 10 years
    if (oldestDate) {
      const weeksDiff = Math.ceil((today.getTime() - oldestDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
      weeksToGoBack = Math.max(weeksDiff + 1, 52); // At least 1 year, but include all data
    }
    
    // Start from the most recent Sunday
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay());
    
    // Generate week ranges going back
    for (let i = 0; i < weeksToGoBack; i++) {
      const sunday = new Date(lastSunday);
      sunday.setDate(lastSunday.getDate() - (i * 7));
      
      // Only include past dates
      if (sunday <= today) {
        const weekRange = getWeekRange(sunday);
        const weekColumn = formatWeekColumn(weekRange.startStr, weekRange.endStr);
        weekRanges.push({
          start: weekRange.start,
          end: weekRange.end,
          startStr: weekRange.startStr,
          endStr: weekRange.endStr,
          label: formatWeekRange(weekRange.startStr, weekRange.endStr),
          yearWeek: weekColumn.yearWeek,
          dateRange: weekColumn.dateRange
        });
      }
    }
    
    return weekRanges;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances]); // Recalculate when balances change

  // Helper function to check if a date falls within a week range
  const isDateInWeek = (dateStr: string, weekStart: string, weekEnd: string): boolean => {
    try {
      // Handle both date strings and datetime strings
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date in isDateInWeek: ${dateStr}`);
        return false;
      }
      
      const start = new Date(weekStart);
      start.setHours(0, 0, 0, 0); // Start of week start date
      const end = new Date(weekEnd);
      end.setHours(23, 59, 59, 999); // End of week end date
      
      // Compare dates (ignore time for week matching, but include full day)
      const dateOnly = new Date(date);
      dateOnly.setHours(12, 0, 0, 0); // Use noon to avoid timezone edge cases
      
      return dateOnly >= start && dateOnly <= end;
    } catch (e) {
      console.error(`Error in isDateInWeek for date ${dateStr}:`, e);
      return false;
    }
  };

  // Helper function to get all balances for specific account/loan within a week range
  const getBalancesForWeek = (type: 'account' | 'loan', id: number, weekStart: string, weekEnd: string) => {
    return balances.filter(b => {
      const matchesType = (type === 'account' && b.account_id === id) || (type === 'loan' && b.loan_id === id);
      if (!matchesType) return false;
      
      // Parse date_entered (could be date string or datetime string)
      try {
        const balanceDate = new Date(b.date_entered);
        if (isNaN(balanceDate.getTime())) {
          console.warn(`Invalid date_entered for balance ${b.id}: ${b.date_entered}`);
          return false;
        }
        
        // Use the full datetime for matching (not just date) to ensure accuracy
        const balanceDateStr = balanceDate.toISOString();
        return isDateInWeek(balanceDateStr, weekStart, weekEnd);
      } catch (e) {
        console.error(`Error parsing date_entered for balance ${b.id}:`, e);
        return false;
      }
    });
  };

  // Helper function to get the latest balance across ALL weeks for an account/loan
  const getLatestBalance = (type: 'account' | 'loan', id: number): ManualBalance | null => {
    const relevantBalances = balances.filter(b => {
      return (type === 'account' && b.account_id === id) || (type === 'loan' && b.loan_id === id);
    });
    
    if (relevantBalances.length === 0) return null;
    
    // Sort by date_entered descending (most recent first)
    relevantBalances.sort((a, b) => {
      const dateA = new Date(a.date_entered).getTime();
      const dateB = new Date(b.date_entered).getTime();
      return dateB - dateA;
    });
    
    return relevantBalances[0]; // Return the most recent one
  };

  // Helper function to get the most recent balance for a week (for display purposes)
  // This is used internally for building the cache - use balanceLookupCache for lookups in render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getMostRecentBalanceForWeek = useCallback((type: 'account' | 'loan', id: number, weekStart: string, weekEnd: string) => {
    const weekBalances = getBalancesForWeek(type, id, weekStart, weekEnd);
    if (weekBalances.length === 0) return null;
    
    // Sort by date_entered descending (most recent first)
    weekBalances.sort((a, b) => {
      const dateA = new Date(a.date_entered).getTime();
      const dateB = new Date(b.date_entered).getTime();
      return dateB - dateA;
    });
    
    return weekBalances[0]; // Return the most recent one
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances]);

  // Helper function to get HTB Mortgage balance (direct entry only - no mirroring)
  const getHTBMortgageBalance = useCallback((weekStart: string, weekEnd: string): ManualBalance | null => {
    // Get the HTB Mortgage loan balance entry directly
    const htbMortgageLoan = loans.find(loan => loan.loan_name.includes('HTB Mortgage') && loan.loan_type === 'mortgage');
    if (!htbMortgageLoan) return null;
    
    return getMostRecentBalanceForWeek('loan', htbMortgageLoan.id, weekStart, weekEnd);
  }, [loans, getMostRecentBalanceForWeek]);

  // GBP to EUR conversion rate (update as needed)
  const GBP_TO_EUR_RATE = 1.16;

  // Helper function to convert amount to EUR
  const convertToEUR = useCallback((amount: number, currency: string): number => {
    if (currency === 'GBP') {
      return amount * GBP_TO_EUR_RATE;
    }
    return amount; // Already EUR or other currency treated as EUR
  }, []);

  // Helper function to get "Vs last Entry" - difference between this row's net position and the previous row's
  // Previous row = chronologically next row in the table (filteredWeekRanges is newest-first)
  const getVsLastEntryDifference = (
    weekStart: string,
    weekEnd: string,
    previousWeekRange: { startStr: string; endStr: string } | null
  ): number | null => {
    if (!previousWeekRange) return null;
    const current = getNetPosition(weekStart, weekEnd);
    const previous = getNetPosition(previousWeekRange.startStr, previousWeekRange.endStr);
    return vsLastEntryDifference(current, previous);
  };


  const formatCurrency = (amount: number, currency: string) => {
    const validCurrency = currency && currency.trim() !== '' ? currency : 'EUR';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: validCurrency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      }).format(amount);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Download functions
  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const exportToCSV = () => {
    handleDownloadMenuClose();
    
    // Build CSV header
    const headers = ['Year - Week No.', 'Date Range'];
    accounts.forEach(account => headers.push(accountExportHeader(account)));
    loans.filter(loan => loan.loan_type === 'mortgage').forEach(loan => {
      let cleanName = loan.loan_name;
      if (cleanName.includes('Mortgage with ')) {
        cleanName = cleanName.replace('Mortgage with ', '');
      }
      headers.push(cleanName);
    });
    loans.filter(loan => loan.loan_type !== 'mortgage').forEach(loan => {
      let cleanName = loan.loan_name;
      if (cleanName.includes('Loan from ')) {
        cleanName = cleanName.replace('Loan from ', '');
      }
      headers.push(cleanName);
    });
    headers.push('Net Position', 'Best Ever', 'Vs last Entry', 'vs Best Ever');
    
    // Build CSV rows
    const rows: string[][] = [];
    filteredWeekRanges.forEach((weekRange, index) => {
      const row: string[] = [
        weekRange.yearWeek,
        weekRange.dateRange
      ];
      
      // Account balances
      accounts.forEach(account => {
        const balance = getCachedBalanceForWeek('account', account.id, weekRange.startStr, weekRange.endStr);
        if (balance) {
          const balanceValue = Math.abs(balance.balance);
          row.push(formatCurrency(balanceValue, balance.currency));
        } else {
          row.push('');
        }
      });
      
      // Mortgage balances
      loans.filter(loan => loan.loan_type === 'mortgage').forEach(loan => {
        const isHTBMortgage = loan.loan_name.includes('HTB Mortgage');
        let balance: ManualBalance | null = null;
        
        if (isHTBMortgage) {
          balance = getHTBMortgageBalance(weekRange.startStr, weekRange.endStr);
        } else {
          balance = getCachedBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
        }
        
        if (balance) {
          const balanceValue = -Math.abs(balance.balance);
          const currency = isHTBMortgage ? 'GBP' : balance.currency;
          row.push(formatCurrency(balanceValue, currency));
        } else {
          row.push('');
        }
      });
      
      // Loan balances
      loans.filter(loan => loan.loan_type !== 'mortgage').forEach(loan => {
        const balance = getCachedBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
        if (balance) {
          const balanceValue = -Math.abs(balance.balance);
          row.push(formatCurrency(balanceValue, balance.currency));
        } else {
          row.push('');
        }
      });
      
      // Net Position
      const netPosition = getNetPosition(weekRange.startStr, weekRange.endStr);
      row.push(formatCurrency(netPosition, 'EUR'));
      
      // Best Ever
      const bestEverWeek = getBestEverNetPosition;
      const isBestEver = bestEverWeek && bestEverWeek.startStr === weekRange.startStr && bestEverWeek.endStr === weekRange.endStr;
      row.push(isBestEver ? 'Best Ever' : '');
      
      // Vs last Entry
      const prevWeek = index + 1 < filteredWeekRanges.length ? filteredWeekRanges[index + 1] : null;
      const vsLastEntry = getVsLastEntryDifference(weekRange.startStr, weekRange.endStr, prevWeek);
      row.push(vsLastEntry !== null ? formatCurrency(vsLastEntry, 'EUR') : '');
      
      // vs Best Ever
      const vsBestEver = bestEverWeek ? netPosition - bestEverWeek.value : null;
      row.push(vsBestEver !== null ? formatCurrency(vsBestEver, 'EUR') : '');
      
      rows.push(row);
    });
    
    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `balances-manual-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('CSV file downloaded successfully');
  };

  const exportToPDF = async () => {
    handleDownloadMenuClose();
    
    try {
      // Dynamically import jspdf and jspdf-autotable
      const { default: jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      
      const doc = new jsPDF('landscape');
      
      // Title
      doc.setFontSize(16);
      doc.text('Balances Manual', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
      
      // Build table data
      const headers = ['Year - Week', 'Date Range'];
      accounts.forEach(account => headers.push(accountExportHeader(account)));
      loans.filter(loan => loan.loan_type === 'mortgage').forEach(loan => {
        let cleanName = loan.loan_name;
        if (cleanName.includes('Mortgage with ')) {
          cleanName = cleanName.replace('Mortgage with ', '');
        }
        headers.push(cleanName);
      });
      loans.filter(loan => loan.loan_type !== 'mortgage').forEach(loan => {
        let cleanName = loan.loan_name;
        if (cleanName.includes('Loan from ')) {
          cleanName = cleanName.replace('Loan from ', '');
        }
        headers.push(cleanName);
      });
      headers.push('Net Position', 'Best Ever', 'Vs Last Entry', 'vs Best Ever');
      
      const rows: (string | number)[][] = [];
      filteredWeekRanges.forEach((weekRange, index) => {
        const row: (string | number)[] = [
          weekRange.yearWeek,
          weekRange.dateRange
        ];
        
        // Account balances
        accounts.forEach(account => {
          const balance = getCachedBalanceForWeek('account', account.id, weekRange.startStr, weekRange.endStr);
          if (balance) {
            const balanceValue = Math.abs(balance.balance);
            row.push(formatCurrency(balanceValue, balance.currency));
          } else {
            row.push('');
          }
        });
        
        // Mortgage balances
        loans.filter(loan => loan.loan_type === 'mortgage').forEach(loan => {
          const isHTBMortgage = loan.loan_name.includes('HTB Mortgage');
          let balance: ManualBalance | null = null;
          
          if (isHTBMortgage) {
            balance = getHTBMortgageBalance(weekRange.startStr, weekRange.endStr);
          } else {
            balance = getCachedBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
          }
          
          if (balance) {
            const balanceValue = -Math.abs(balance.balance);
            const currency = isHTBMortgage ? 'GBP' : balance.currency;
            row.push(formatCurrency(balanceValue, currency));
          } else {
            row.push('');
          }
        });
        
        // Loan balances
        loans.filter(loan => loan.loan_type !== 'mortgage').forEach(loan => {
          const balance = getCachedBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
          if (balance) {
            const balanceValue = -Math.abs(balance.balance);
            row.push(formatCurrency(balanceValue, balance.currency));
          } else {
            row.push('');
          }
        });
        
        // Net Position
        const netPositionPDF = getNetPosition(weekRange.startStr, weekRange.endStr);
        row.push(formatCurrency(netPositionPDF, 'EUR'));
        
        // Best Ever
        const bestEverWeekPDF = getBestEverNetPosition;
        const isBestEverPDF = bestEverWeekPDF && bestEverWeekPDF.startStr === weekRange.startStr && bestEverWeekPDF.endStr === weekRange.endStr;
        row.push(isBestEverPDF ? 'Best Ever' : '');
        
        // Vs last Entry
        const prevWeekPDF = index + 1 < filteredWeekRanges.length ? filteredWeekRanges[index + 1] : null;
        const vsLastEntryPDF = getVsLastEntryDifference(weekRange.startStr, weekRange.endStr, prevWeekPDF);
        row.push(vsLastEntryPDF !== null ? formatCurrency(vsLastEntryPDF, 'EUR') : '');
        
        // vs Best Ever
        const vsBestEverPDF = bestEverWeekPDF ? netPositionPDF - bestEverWeekPDF.value : null;
        row.push(vsBestEverPDF !== null ? formatCurrency(vsBestEverPDF, 'EUR') : '');
        
        rows.push(row);
      });
      
      // Add table
      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [66, 66, 66], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 28 },
      });
      
      // Save PDF
      doc.save(`balances-manual-${new Date().toISOString().split('T')[0]}.pdf`);
      showToast('PDF file downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  // Helper function to determine currency based on account/loan name
  const getCurrencyForAccountOrLoan = (name: string): string => {
    // Rev Biz UK and HTB Mortgage are GBP, everything else is EUR
    if (name.includes('Rev Biz UK') || name.includes('HTB Mortgage')) {
      return 'GBP';
    }
    return 'EUR';
  };

  const handleCellClick = (type: 'account' | 'loan', id: number, weekStart: string, weekEnd: string, name: string) => {
    if (!canManage) return; // Only allow editing if user can manage
    
    // Get the most recent balance for this week, or use a date from this week
    const existingBalance = getMostRecentBalanceForWeek(type, id, weekStart, weekEnd);
    const lockedCurrency = getCurrencyForAccountOrLoan(name);
    
    // Default to a date within the clicked row's week (use weekStart, which is Sunday)
    // If there's an existing balance, use its date/time; otherwise use the week's start date
    let dateValue = weekStart; // Default to the start of the week (Sunday)
    let timeValue = '12:00'; // Default to noon
    
    if (existingBalance) {
      // Extract date and time from date_entered (now a datetime)
      if (existingBalance.date_entered) {
        const dateEntered = new Date(existingBalance.date_entered);
        dateValue = dateEntered.toISOString().split('T')[0]; // YYYY-MM-DD
        // Use getHours() and getMinutes() to get local time values correctly
        const hours = dateEntered.getHours();
        const minutes = dateEntered.getMinutes();
        timeValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`; // HH:MM
      }
    }
    
    setSelectedCell({ type, id, date: weekStart, name }); // Store weekStart as date for compatibility
    // Format balance with commas when loading existing balance
    const formattedBalance = existingBalance 
      ? existingBalance.balance.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        })
      : '';
    setIsAddNewMode(false); // Not in add new mode when clicking a cell
    setFormData({
      date_entered: dateValue,
      time_entered: timeValue,
      balance: formattedBalance,
      currency: lockedCurrency, // Always use the locked currency
      notes: existingBalance ? existingBalance.notes || '' : '',
      account_id: null,
      loan_id: null,
    });
    setDialogOpen(true);
  };

  const handleAddNewEntry = () => {
    if (!canManage) return;
    
    const now = new Date();
    setIsAddNewMode(true);
    setSelectedCell(null); // No specific cell selected
    setFormData({
      date_entered: now.toISOString().split('T')[0],
      time_entered: `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`,
      balance: '',
      currency: 'EUR',
      notes: '',
      account_id: null,
      loan_id: null,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCell(null);
    setIsAddNewMode(false);
    const now = new Date();
    setFormData({
      date_entered: now.toISOString().split('T')[0],
      time_entered: now.toTimeString().slice(0, 5),
      balance: '',
      currency: 'EUR',
      notes: '',
      account_id: null,
      loan_id: null,
    });
  };

  const handleCloseAddEntityDialog = () => {
    setAddEntityDialogOpen(false);
    setEntityFormData({
      name: '',
      category: 'business_bank_account',
      account_number: '',
      bank_name: '',
      lender: '',
      original_amount: '',
      current_balance: '',
      interest_rate: '',
      monthly_payment: '',
    });
  };

  const handleAddEntity = async () => {
    try {
      const category = entityFormData.category;
      const isAccount = category === 'business_bank_account' || category === 'personal_bank_account' || category === 'other';
      const isLoan = category === 'mortgage' || category === 'business_loan' || category === 'personal_loan';

      if (isAccount) {
        if (!entityFormData.name || !entityFormData.account_number) {
          setError('Name and account number are required');
          return;
        }

        const accountType = category === 'business_bank_account' ? 'business' : 
                           category === 'personal_bank_account' ? 'personal' : 'business';

        const accountData = {
          account_name: entityFormData.name,
          account_number: entityFormData.account_number,
          bank_name: entityFormData.bank_name || '',
          account_type: accountType,
          current_balance: 0,
        };

        await api.post('/api/accounts/', accountData);
        showToast(`${category === 'other' ? 'Account' : category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} "${entityFormData.name}" created successfully`);
      } else if (isLoan) {
        if (!entityFormData.name || !entityFormData.lender) {
          setError('Name and lender are required');
          return;
        }

        const loanTypeMap: { [key: string]: string } = {
          'mortgage': 'mortgage',
          'business_loan': 'business',
          'personal_loan': 'personal',
        };

        const loanData = {
          loan_name: entityFormData.name,
          lender: entityFormData.lender,
          loan_type: loanTypeMap[category],
          original_amount: parseFloat(entityFormData.original_amount) || 0,
          current_balance: parseFloat(entityFormData.current_balance) || 0,
          interest_rate: parseFloat(entityFormData.interest_rate) || 0,
          monthly_payment: parseFloat(entityFormData.monthly_payment) || 0,
        };

        await api.post('/api/loans/', loanData);
        showToast(`${category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} "${entityFormData.name}" created successfully`);
      } else {
        setError('Invalid category selected');
        return;
      }

      handleCloseAddEntityDialog();
      fetchData(); // Refresh the data to show the new account/loan
    } catch (err: any) {
      console.error('Error creating entity:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create account/loan');
    }
  };

  const handleSubmit = async () => {
    // In add new mode, account/loan must be selected via form
    if (!isAddNewMode && !selectedCell) {
      console.error('handleSubmit: selectedCell is null');
      setError('No account/loan selected');
      return;
    }
    
    // In add new mode, validate that account_id or loan_id is provided
    if (isAddNewMode) {
      if (!formData.account_id && !formData.loan_id) {
        setError('Please select an account or loan');
        return;
      }
      if (formData.account_id && formData.loan_id) {
        setError('Please select either an account or a loan, not both');
        return;
      }
    }

    try {
      setError(''); // Clear any previous errors
      
      console.log('handleSubmit called with:', {
        selectedCell,
        formData,
        date_entered: formData.date_entered,
        time_entered: formData.time_entered,
        balance: formData.balance
      });
      
      // Validate required fields
      if (!formData.date_entered || !formData.time_entered) {
        const errorMsg = 'Please enter both date and time';
        console.error('Validation failed:', errorMsg);
        setError(errorMsg);
        return;
      }

      // Remove commas and any currency symbols from balance before checking
      const balanceStr = formData.balance.replace(/,/g, '').replace(/[£€$]/g, '').trim();
      const balanceValue = parseFloat(balanceStr);
      
      console.log('Balance parsing:', {
        original: formData.balance,
        cleaned: balanceStr,
        parsed: balanceValue,
        isNaN: isNaN(balanceValue),
        isZero: balanceValue === 0
      });
      
      if (!formData.balance || balanceStr === '' || isNaN(balanceValue)) {
        const errorMsg = 'Please enter a valid balance';
        console.error('Validation failed:', errorMsg);
        setError(errorMsg);
        return;
      }

      // Determine account/loan ID based on mode
      let accountId: number | null = null;
      let loanId: number | null = null;
      let balanceType: 'account' | 'loan' | null = null;
      let entityName = '';
      let entityType = '';
      
      if (isAddNewMode) {
        accountId = formData.account_id || null;
        loanId = formData.loan_id || null;
        balanceType = accountId ? 'account' : (loanId ? 'loan' : null);
        
        // Get entity name
        if (accountId) {
          const account = accounts.find(a => a.id === accountId);
          entityName = account?.account_name || 'Account';
          entityType = 'Bank Account';
        } else if (loanId) {
          const loan = loans.find(l => l.id === loanId);
          entityName = loan?.loan_name || 'Loan';
          // Determine loan type for display
          if (loan?.loan_type === 'mortgage') {
            entityType = 'Mortgage';
          } else if (loan?.loan_type === 'business_loan') {
            entityType = 'Business Loan';
          } else if (loan?.loan_type === 'personal_loan') {
            entityType = 'Personal Loan';
          } else {
            entityType = 'Loan';
          }
        }
      } else {
        if (!selectedCell) {
          setError('No account/loan selected');
          return;
        }
        entityName = selectedCell.name;
        if (selectedCell.type === 'account') {
          accountId = selectedCell.id;
          balanceType = 'account';
          entityType = 'Bank Account';
        } else {
          loanId = selectedCell.id;
          balanceType = 'loan';
          // Determine loan type for display
          const loan = loans.find(l => l.id === loanId);
          if (loan?.loan_type === 'mortgage') {
            entityType = 'Mortgage';
          } else if (loan?.loan_type === 'business_loan') {
            entityType = 'Business Loan';
          } else if (loan?.loan_type === 'personal_loan') {
            entityType = 'Personal Loan';
          } else {
            entityType = 'Loan';
          }
        }
      }
      
      // Format date for display
      const dateDisplay = new Date(`${formData.date_entered}T${formData.time_entered}:00`).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      // Format balance for display
      const balanceDisplay = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: formData.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(balanceValue);
      
      // Close modal immediately and show progress message
      handleCloseDialog();
      showToast(`Adding Balance ${balanceDisplay} to ${entityType} "${entityName}" on ${dateDisplay}...`);
      
      // Find existing balance - check if there's one in the same week as the entered date
      const enteredDate = new Date(`${formData.date_entered}T${formData.time_entered}:00`);
      const enteredWeekRange = getWeekRange(enteredDate);
      
      // Get all balances for this account/loan in the entered week
      let existingBalance: ManualBalance | null = null;
      if (balanceType && (accountId || loanId)) {
        const weekBalances = getBalancesForWeek(
          balanceType, 
          accountId || loanId || 0, 
          enteredWeekRange.startStr, 
          enteredWeekRange.endStr
        );
        
        // If editing, find the balance that matches the entered date/time (or use most recent)
        if (weekBalances.length > 0) {
          // Try to find exact match first, otherwise use most recent
          existingBalance = weekBalances.find(b => {
            const bDate = new Date(b.date_entered);
            return bDate.getTime() === enteredDate.getTime();
          }) || weekBalances[0];
        }
      }

      // Combine date and time into date_entered
      // balanceValue already parsed above
      const payload: any = {
        balance: balanceValue,
        currency: formData.currency,
        notes: formData.notes,
        date_entered: formData.date_entered,
        time_entered: formData.time_entered,
      };

      if (accountId) {
        payload.account_id = accountId;
      } else if (loanId) {
        payload.loan_id = loanId;
      }

      console.log('Submitting payload:', payload);

      // Optimistically update the UI immediately
      const optimisticBalance: ManualBalance = {
        id: existingBalance?.id || Date.now(), // Temporary ID
        user_id: existingBalance?.user_id || 0, // Will be set by backend, use 0 as placeholder
        account_id: accountId ?? undefined,
        loan_id: loanId ?? undefined,
        balance: balanceValue,
        currency: formData.currency,
        notes: formData.notes || '',
        date_entered: `${formData.date_entered}T${formData.time_entered}:00`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        account_name: accountId ? entityName : undefined,
        loan_name: loanId ? entityName : undefined,
      };
      
      // Add to local state immediately for instant UI update
      const balanceToUpdate = existingBalance; // Capture for closure
      setBalances(prev => {
        if (balanceToUpdate) {
          // Update existing balance
          return prev.map(b => b.id === balanceToUpdate.id ? optimisticBalance : b);
        } else {
          // Add new balance
          return [optimisticBalance, ...prev];
        }
      });
      
      // Make API call in background with shorter timeout for POST/PUT
      const apiTimeout = { timeout: 15000 }; // 15 seconds for POST/PUT (should be fast)
      const balanceForApi = existingBalance; // Capture for async closure
      
      (async () => {
        try {
          let response: any;
          if (balanceForApi) {
            console.log('Updating existing balance:', balanceForApi.id);
            response = await api.put(`/api/manual-balances/${balanceForApi.id}`, payload, apiTimeout);
            showToast(`Balance ${balanceDisplay} updated successfully for ${entityType} "${entityName}"`);
          } else {
            console.log('Creating new balance');
            response = await api.post('/api/manual-balances/', payload, apiTimeout);
            showToast(`Balance ${balanceDisplay} added successfully to ${entityType} "${entityName}"`);
            
            // Update optimistic balance with real ID from response
            if (response.data?.balance?.id) {
              setBalances(prev => prev.map(b => 
                b.id === optimisticBalance.id ? { ...b, id: response.data.balance.id } : b
              ));
            }
          }
          
          // Refresh only balances (not accounts/loans) in background - non-blocking
          setTimeout(async () => {
            try {
              const balancesRes = await api.get('/api/manual-balances/', { timeout: 10000 });
              setBalances(balancesRes.data.balances || []);
            } catch (refreshErr) {
              console.error('Background refresh failed (non-critical):', refreshErr);
            }
          }, 500); // Small delay to let the server process
          
        } catch (err: any) {
          console.error('Error saving manual balance:', err);
          const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to save manual balance';
          
          // Revert optimistic update on error
          const balanceToRevert = existingBalance; // Capture for closure
          setBalances(prev => {
            if (balanceToRevert) {
              // Restore original balance
              return prev.map(b => b.id === optimisticBalance.id ? balanceToRevert : b);
            } else {
              // Remove optimistic balance
              return prev.filter(b => b.id !== optimisticBalance.id);
            }
          });
          
          // Check if it's a timeout but the request might have succeeded
          if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            showToast(`Balance may have been saved (timeout occurred). Please refresh to verify.`);
            // Refresh data in case it was saved
            setTimeout(async () => {
              try {
                const balancesRes = await api.get('/api/manual-balances/', { timeout: 10000 });
                setBalances(balancesRes.data.balances || []);
              } catch (refreshErr) {
                console.error('Background refresh failed:', refreshErr);
              }
            }, 1000);
          } else {
            showToast(`Failed to save balance: ${errorMsg}`);
          }
        }
      })();
    } catch (err: any) {
      console.error('Error in handleSubmit validation:', err);
      const errorMsg = err.message || 'Failed to validate balance entry';
      setError(errorMsg);
    }
  };

  const handleDeleteAllBalancesForWeek = async (weekStart: string, weekEnd: string) => {
    try {
      setError('');
      
      // Optimistically remove balances for this week from state
      const balancesToDelete = balances.filter(b => {
        const balanceDate = b.date_entered;
        return balanceDate >= weekStart && balanceDate <= weekEnd;
      });
      
      // Store original balances for potential revert
      const originalBalances = [...balances];
      
      // Optimistically remove from state immediately
      setBalances(prev => prev.filter(b => {
        const balanceDate = b.date_entered;
        return !(balanceDate >= weekStart && balanceDate <= weekEnd);
      }));
      
      // Show toast immediately
      showToast(`Deleting ${balancesToDelete.length} balance entries for this week...`);
      
      // Make API call asynchronously in background
      (async () => {
        try {
          const response = await api.delete('/api/manual-balances/by-week', {
            data: {
              week_start: weekStart,
              week_end: weekEnd
            },
            timeout: 15000 // 15 seconds timeout
          });
          
          if (response.data.success) {
            showToast(response.data.message || `Successfully deleted ${response.data.deleted_count || 0} balance entries`);
            
            // Refresh only balances in background - non-blocking
            setTimeout(async () => {
              try {
                const balancesRes = await api.get('/api/manual-balances/', { timeout: 10000 });
                setBalances(balancesRes.data.balances || []);
              } catch (refreshErr) {
                console.error('Background refresh failed (non-critical):', refreshErr);
              }
            }, 500); // Small delay to let the server process
          } else {
            // Revert optimistic update on failure
            setBalances(originalBalances);
            setError(response.data.message || 'Failed to delete balances');
          }
        } catch (err: any) {
          console.error('Error deleting balances for week:', err);
          
          // Revert optimistic update on error
          setBalances(originalBalances);
          
          const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete balances for this week';
          setError(errorMsg);
          
          // Check if it's a timeout but the request might have succeeded
          if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            showToast(`Deletion may have succeeded (timeout occurred). Please refresh to verify.`);
          } else {
            showToast(errorMsg);
          }
        }
      })();
      
    } catch (err: any) {
      console.error('Error in handleDeleteAllBalancesForWeek:', err);
      setError(err.message || 'Failed to delete balances for this week');
    }
  };

  const handleBulkImport = async () => {
    if (bulkImportLoading) return; // Prevent multiple clicks
    
    try {
      setError('');
      setBulkImportLoading(true);
      setBulkImportProgress(null);
      
      if (!bulkImportText.trim()) {
        setError('Please paste the data to import');
        setBulkImportLoading(false);
        return;
      }

      const lines = bulkImportText.trim().split('\n').filter(line => line.trim());
      if (lines.length < 1) {
        setError('Invalid format. Expected at least one data row.');
        setBulkImportLoading(false);
        return;
      }

      // Detect separator (tab or space) by checking first line
      const firstLine = lines[0];
      const hasTabs = firstLine.includes('\t');
      const separator = hasTabs ? '\t' : /\s{2,}|\t/; // Tab or multiple spaces
      
      // Check if first line is a header or data
      const firstLineParts = hasTabs ? firstLine.split('\t') : firstLine.split(/\s{2,}/);
      const isHeader = firstLineParts.some(part => 
        part.toLowerCase().includes('date') || 
        part.toLowerCase().includes('rev biz') || 
        part.toLowerCase().includes('boi mortgage')
      );
      
      let dateIndex = -1;
      let revBizIreIndex = -1;
      let boiMortgageIndex = -1;
      let dataStartIndex = 0;
      
      if (isHeader) {
        // Parse header to find column indices
        const header = firstLineParts;
        dateIndex = header.findIndex(col => col.toLowerCase().includes('date'));
        revBizIreIndex = header.findIndex(col => col.toLowerCase().includes('rev biz ire'));
        boiMortgageIndex = header.findIndex(col => col.toLowerCase().includes('boi mortgage'));
        dataStartIndex = 1; // Skip header row
      } else {
        // No header - auto-detect column positions based on content
        // Assume format: Date | Rev Biz Ire | BOI Mortgage
        // Date is first column (contains day name or date-like text)
        // Rev Biz Ire is second column (contains € and positive number)
        // BOI Mortgage is third column (contains -€ and negative number)
        dateIndex = 0;
        revBizIreIndex = 1;
        boiMortgageIndex = 2;
        dataStartIndex = 0; // Start from first line
      }

      if (dateIndex === -1) {
        setError('Could not find Date column. Please ensure your data has dates in the first column.');
        setBulkImportLoading(false);
        return;
      }

      // Find account/loan IDs — prefer manual Rev Biz Ire when API account also contains "Rev Biz Ire"
      const revBizIreAccount =
        accounts.find(
          (acc) =>
            /rev biz/i.test(acc.account_name) &&
            /ire/i.test(acc.account_name) &&
            !/rev biz\s+uk/i.test(acc.account_name) &&
            /manual/i.test(acc.account_name)
        ) ||
        accounts.find((acc) => acc.account_name.includes('Rev Biz Ire'));
      const boiMortgageLoan = loans.find(loan => loan.loan_name.includes('BOI Mortgage'));

      if (!revBizIreAccount) {
        setError('Could not find Rev Biz Ire account');
        setBulkImportLoading(false);
        return;
      }
      if (!boiMortgageLoan) {
        setError('Could not find BOI Mortgage loan');
        setBulkImportLoading(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      const errors: string[] = [];
      const totalRows = lines.length - dataStartIndex; // Exclude header if present
      const totalEntries = totalRows * 2; // Each row can have 2 entries (Rev Biz Ire + BOI Mortgage)

      // Helper function to check if a balance already exists
      const balanceExists = (accountId: number | null, loanId: number | null, dateValue: string, timeValue: string): boolean => {
        const dateTimeStr = `${dateValue}T${timeValue}:00`;
        return balances.some(b => {
          // Match account_id or loan_id
          const matchesEntity = (accountId !== null && b.account_id === accountId) || 
                                (loanId !== null && b.loan_id === loanId);
          
          if (!matchesEntity) return false;
          
          // Match date and time (compare date_entered to the target date/time)
          const balanceDate = new Date(b.date_entered);
          const targetDate = new Date(dateTimeStr);
          
          // Compare dates (ignore seconds/milliseconds)
          return balanceDate.getFullYear() === targetDate.getFullYear() &&
                 balanceDate.getMonth() === targetDate.getMonth() &&
                 balanceDate.getDate() === targetDate.getDate() &&
                 balanceDate.getHours() === targetDate.getHours() &&
                 balanceDate.getMinutes() === targetDate.getMinutes();
        });
      };

      // Close the modal immediately when import starts
      setBulkImportDialogOpen(false);

      // Process each data row
      for (let i = dataStartIndex; i < lines.length; i++) {
        setBulkImportProgress({ current: i - dataStartIndex + 1, total: totalRows });
        
        // Split row using detected separator
        const row = hasTabs 
          ? lines[i].split('\t')
          : lines[i].split(/\s{2,}/).map(col => col.trim()).filter(col => col.length > 0);
        
        // If space-separated and we got fewer columns than expected, try splitting by single space
        // but be smarter about it - dates have spaces, so we need to be careful
        if (!hasTabs && row.length < 3) {
          // Try to parse: "Wednesday, 5 February 2025 €155,829.30 -€423,635.31"
          // Split by spaces, but group date parts together
          const line = lines[i];
          // Match date pattern (e.g., "Wednesday, 5 February 2025")
          const dateMatch = line.match(/^([A-Za-z]+,\s+\d{1,2}\s+[A-Za-z]+\s+\d{4})/);
          if (dateMatch) {
            const datePart = dateMatch[1];
            const rest = line.substring(dateMatch[0].length).trim();
            // Split rest by spaces, but keep currency symbols with numbers
            const restParts = rest.split(/\s+/);
            row.length = 0; // Clear array
            row.push(datePart); // Date is first column
            // Find Rev Biz Ire (starts with €)
            const revBizPart = restParts.find(p => p.startsWith('€') && !p.startsWith('-€'));
            if (revBizPart) row.push(revBizPart);
            // Find BOI Mortgage (starts with -€)
            const boiPart = restParts.find(p => p.startsWith('-€'));
            if (boiPart) row.push(boiPart);
          }
        }
        if (row.length < Math.max(dateIndex, revBizIreIndex !== -1 ? revBizIreIndex : 0, boiMortgageIndex !== -1 ? boiMortgageIndex : 0) + 1) {
          errors.push(`Row ${i - dataStartIndex + 1}: Invalid format (not enough columns). Found ${row.length} columns, expected at least ${Math.max(dateIndex, revBizIreIndex !== -1 ? revBizIreIndex : 0, boiMortgageIndex !== -1 ? boiMortgageIndex : 0) + 1}`);
          errorCount++;
          continue;
        }

        const dateStr = row[dateIndex]?.trim();
        if (!dateStr) {
          errors.push(`Row ${i - dataStartIndex + 1}: Missing date`);
          errorCount++;
          continue;
        }

        // Parse date (format: "Sunday, 28 July 2019")
        // Try multiple date parsing approaches
        let date: Date | null = null;
        let dateValue: string = '';
        
        try {
          // First try: JavaScript Date constructor (handles most formats including "Sunday, 28 July 2019")
          date = new Date(dateStr);
          
          // Check if date is valid
          if (isNaN(date.getTime())) {
            // Second try: Parse manually for "Day, DD Month YYYY" format
            // Match patterns like "28 July 2019" or "Sunday, 28 July 2019"
            const dateMatch = dateStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
            if (dateMatch) {
              const [, day, monthName, year] = dateMatch;
              const monthMap: { [key: string]: string } = {
                'january': '01', 'jan': '01',
                'february': '02', 'feb': '02',
                'march': '03', 'mar': '03',
                'april': '04', 'apr': '04',
                'may': '05',
                'june': '06', 'jun': '06',
                'july': '07', 'jul': '07',
                'august': '08', 'aug': '08',
                'september': '09', 'sep': '09', 'sept': '09',
                'october': '10', 'oct': '10',
                'november': '11', 'nov': '11',
                'december': '12', 'dec': '12'
              };
              const month = monthMap[monthName.toLowerCase()];
              if (month) {
                // Create date string directly to avoid timezone issues
                dateValue = `${year}-${month}-${day.padStart(2, '0')}`;
                date = new Date(dateValue + 'T12:00:00'); // Use noon to avoid timezone edge cases
              }
            }
          }
          
          // If we don't have dateValue yet, extract from the parsed date
          if (!dateValue && date && !isNaN(date.getTime())) {
            // Extract year, month, day to avoid timezone issues
            // Use local date components to ensure the date doesn't shift
            const year = date.getFullYear();
            const month = date.getMonth(); // 0-indexed
            const day = date.getDate();
            
            // Create date string in YYYY-MM-DD format
            // This ensures the date doesn't shift due to timezone conversion
            dateValue = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
          
          // Final validation
          if (!date || isNaN(date.getTime()) || !dateValue) {
            console.error(`Row ${i - dataStartIndex + 1}: Failed to parse date: ${dateStr}`);
            errors.push(`Row ${i - dataStartIndex + 1}: Invalid date format: ${dateStr}`);
            errorCount++;
            continue;
          }
          
          console.log(`Row ${i - dataStartIndex + 1}: Parsed date "${dateStr}" as ${dateValue}`);
        } catch (e) {
          console.error(`Row ${i - dataStartIndex + 1}: Exception parsing date: ${dateStr}`, e);
          errors.push(`Row ${i - dataStartIndex + 1}: Invalid date format: ${dateStr}`);
          errorCount++;
          continue;
        }

        const timeValue = '12:00'; // Default to noon

        // Process Rev Biz Ire balance
        if (revBizIreIndex !== -1 && row[revBizIreIndex] && row[revBizIreIndex].trim()) {
          const balanceStr = row[revBizIreIndex].trim().replace(/[€,\s]/g, '');
          const balance = parseFloat(balanceStr);
          if (!isNaN(balance) && balance !== 0) {
            // Check if balance already exists
            if (balanceExists(revBizIreAccount.id, null, dateValue, timeValue)) {
              skippedCount++;
              console.log(`⊘ Row ${i - dataStartIndex + 1}: Skipped Rev Biz Ire balance (already exists)`);
            } else {
              try {
                await api.post('/api/manual-balances/', {
                  account_id: revBizIreAccount.id,
                  balance: Math.abs(balance),
                  currency: 'EUR',
                  date_entered: dateValue,
                  time_entered: timeValue,
                });
                successCount++;
                console.log(`✓ Row ${i - dataStartIndex + 1}: Imported Rev Biz Ire balance ${balance}`);
              } catch (err: any) {
                const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
                errors.push(`Row ${i - dataStartIndex + 1} Rev Biz Ire: ${errorMsg}`);
                errorCount++;
                console.error(`✗ Row ${i - dataStartIndex + 1} Rev Biz Ire error:`, errorMsg);
              }
            }
          }
        }

        // Process BOI Mortgage balance
        if (boiMortgageIndex !== -1 && row[boiMortgageIndex] && row[boiMortgageIndex].trim()) {
          const balanceStr = row[boiMortgageIndex].trim().replace(/[€,\s-]/g, '');
          const balance = parseFloat(balanceStr);
          if (!isNaN(balance) && balance !== 0) {
            // Check if balance already exists
            if (balanceExists(null, boiMortgageLoan.id, dateValue, timeValue)) {
              skippedCount++;
              console.log(`⊘ Row ${i - dataStartIndex + 1}: Skipped BOI Mortgage balance (already exists)`);
            } else {
              try {
                await api.post('/api/manual-balances/', {
                  loan_id: boiMortgageLoan.id,
                  balance: Math.abs(balance), // Store as positive, display will show negative
                  currency: 'EUR',
                  date_entered: dateValue,
                  time_entered: timeValue,
                });
                successCount++;
                console.log(`✓ Row ${i - dataStartIndex + 1}: Imported BOI Mortgage balance ${balance}`);
              } catch (err: any) {
                const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
                errors.push(`Row ${i - dataStartIndex + 1} BOI Mortgage: ${errorMsg}`);
                errorCount++;
                console.error(`✗ Row ${i - dataStartIndex + 1} BOI Mortgage error:`, errorMsg);
              }
            }
          }
        }
      }

      setBulkImportProgress(null);
      setBulkImportLoading(false);

      // Show detailed results
      let resultMessage = '';
      if (successCount > 0 || skippedCount > 0 || errorCount > 0) {
        const parts: string[] = [];
        if (successCount > 0) {
          parts.push(`${successCount} imported`);
        }
        if (skippedCount > 0) {
          parts.push(`${skippedCount} skipped (already exist)`);
        }
        if (errorCount > 0) {
          parts.push(`${errorCount} errors`);
        }
        resultMessage = parts.join(', ');
      }

      if (errorCount > 0) {
        const errorSummary = errors.length > 10 
          ? errors.slice(0, 10).join('; ') + ` ... and ${errors.length - 10} more errors`
          : errors.join('; ');
        setError(`${resultMessage}:\n${errorSummary}`);
      } else if (skippedCount > 0 && successCount === 0) {
        setError(`All entries skipped (already exist). ${skippedCount} duplicate entries found.`);
      } else {
        showToast(resultMessage || `Successfully imported ${successCount} balance entries`);
        setBulkImportDialogOpen(false);
        setBulkImportText('');
      }

      // Refresh the data to ensure all imported entries are displayed
      await fetchData();
    } catch (err: any) {
      console.error('Error bulk importing:', err);
      setBulkImportLoading(false);
      setBulkImportProgress(null);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to import balances');
    }
  };

  // Memoize expensive computations for dialog to prevent re-calculation on every keystroke
  // Memoize balance lookups to avoid expensive recalculations on every keystroke
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const balanceLookupCache = useMemo(() => {
    const cache = new Map<string, ManualBalance | null>();
    const weekRanges = getAllWeekRanges;
    
    // Pre-compute all balance lookups for all accounts, loans, and weeks
    accounts.forEach(account => {
      weekRanges.forEach(weekRange => {
        const key = `account-${account.id}-${weekRange.startStr}-${weekRange.endStr}`;
        const balance = getMostRecentBalanceForWeek('account', account.id, weekRange.startStr, weekRange.endStr);
        cache.set(key, balance);
      });
    });
    
    loans.forEach(loan => {
      weekRanges.forEach(weekRange => {
        const key = `loan-${loan.id}-${weekRange.startStr}-${weekRange.endStr}`;
        const balance = getMostRecentBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
        cache.set(key, balance);
      });
    });
    
    return cache;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances, accounts, loans, getMostRecentBalanceForWeek, getAllWeekRanges]);

  // Memoized lookup function that uses the cache - defined after balanceLookupCache
  const getCachedBalanceForWeek = useCallback((type: 'account' | 'loan', id: number, weekStart: string, weekEnd: string) => {
    const cacheKey = `${type}-${id}-${weekStart}-${weekEnd}`;
    return balanceLookupCache.get(cacheKey) || null;
  }, [balanceLookupCache]);

  // Helper function to check if a week has any balances
  const weekHasBalances = useCallback((weekStart: string, weekEnd: string): boolean => {
    // Check if any account has a balance in this week
    for (const account of accounts) {
      const balance = getCachedBalanceForWeek('account', account.id, weekStart, weekEnd);
      if (balance) return true;
    }
    
    // Check if any loan has a balance in this week
    for (const loan of loans) {
      const balance = getCachedBalanceForWeek('loan', loan.id, weekStart, weekEnd);
      if (balance) return true;
    }
    
    return false;
  }, [accounts, loans, getCachedBalanceForWeek]);

  // Filter week ranges to only show rows with balances
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredWeekRanges = useMemo(() => {
    return getAllWeekRanges.filter(weekRange => weekHasBalances(weekRange.startStr, weekRange.endStr));
  }, [getAllWeekRanges, weekHasBalances, balances, accounts, loans]);

  // Helper function to calculate net position for a week (all in EUR)
  // Formula: Net Position = Sum of all Account balances (in EUR) - Sum of all Loan balances (in EUR)
  // Defined after getCachedBalanceForWeek since it depends on it
  const getNetPosition = useCallback((weekStart: string, weekEnd: string) => {
    let net = 0;
    
    // Step 1: Add all account balances (positive contribution) - converted to EUR
    accounts.forEach(account => {
      const balance = getCachedBalanceForWeek('account', account.id, weekStart, weekEnd);
      if (balance) {
        // Accounts are positive - convert to EUR and add to net
        const balanceInEUR = convertToEUR(Math.abs(balance.balance), balance.currency);
        net += balanceInEUR;
      }
    });
    
    // Step 2: Subtract all mortgage balances first (negative contribution) - converted to EUR
    loans.filter(loan => loan.loan_type === 'mortgage').forEach(loan => {
      const isHTBMortgage = loan.loan_name.includes('HTB Mortgage');
      let balance: ManualBalance | null = null;
      
      if (isHTBMortgage) {
        // HTB Mortgage uses direct entry only (no mirroring)
        balance = getHTBMortgageBalance(weekStart, weekEnd);
      } else {
        balance = getCachedBalanceForWeek('loan', loan.id, weekStart, weekEnd);
      }
      
      if (balance) {
        // Mortgages are debt (negative) - convert to EUR and subtract from net
        // HTB Mortgage uses GBP, so convert it properly
        const currency = isHTBMortgage ? 'GBP' : balance.currency;
        const balanceInEUR = convertToEUR(Math.abs(balance.balance), currency);
        net -= balanceInEUR;
      }
    });
    
    // Step 3: Subtract all loan balances (negative contribution) - converted to EUR
    loans.filter(loan => loan.loan_type !== 'mortgage').forEach(loan => {
      const balance = getCachedBalanceForWeek('loan', loan.id, weekStart, weekEnd);
      
      if (balance) {
        // Loans are debt (negative) - convert to EUR and subtract from net
        const balanceInEUR = convertToEUR(Math.abs(balance.balance), balance.currency);
        net -= balanceInEUR;
      }
    });
    
    return roundToCents(net);
  }, [accounts, loans, getCachedBalanceForWeek, getHTBMortgageBalance, convertToEUR]);

  // Helper function to find the best ever net position across all weeks with balances
  // Memoized to prevent recalculation on every render
  // Only checks weeks that have balances (filteredWeekRanges)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getBestEverNetPosition = useMemo((): { startStr: string; endStr: string; value: number } | null => {
    const weekRanges = filteredWeekRanges;
    let bestEver: { startStr: string; endStr: string; value: number } | null = null;
    let bestValue = -Infinity;
    
    weekRanges.forEach(weekRange => {
      const netPosition = getNetPosition(weekRange.startStr, weekRange.endStr);
      if (netPosition > bestValue) {
        bestValue = netPosition;
        bestEver = {
          startStr: weekRange.startStr,
          endStr: weekRange.endStr,
          value: netPosition
        };
      }
    });
    
    return bestEver;
  }, [filteredWeekRanges, getNetPosition]);

  // Helper function to check if a week is the best ever
  const isBestEverWeek = useCallback((weekStart: string, weekEnd: string): boolean => {
    const bestEver = getBestEverNetPosition;
    if (!bestEver) return false;
    return bestEver.startStr === weekStart && bestEver.endStr === weekEnd;
  }, [getBestEverNetPosition]);

  // Helper function to get difference from best ever
  const getBestEverDifference = useCallback((weekStart: string, weekEnd: string): number | null => {
    const bestEver = getBestEverNetPosition;
    if (!bestEver) return null;
    
    const current = getNetPosition(weekStart, weekEnd);
    
    // If this is the best ever week, return null (no comparison needed)
    if (isBestEverWeek(weekStart, weekEnd)) return null;
    
    const difference = current - bestEver.value;
    return difference;
  }, [getBestEverNetPosition, getNetPosition, isBestEverWeek]);

  const dialogWeekEnd = useMemo(() => {
    if (isAddNewMode || !selectedCell) return null;
    return new Date(new Date(selectedCell.date).getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddNewMode, selectedCell?.date]);

  const dialogExistingBalance = useMemo(() => {
    if (isAddNewMode || !selectedCell || !dialogWeekEnd) return null;
    const cacheKey = `${selectedCell.type}-${selectedCell.id}-${selectedCell.date}-${dialogWeekEnd}`;
    return balanceLookupCache.get(cacheKey) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddNewMode, selectedCell?.type, selectedCell?.id, selectedCell?.date, dialogWeekEnd, balanceLookupCache]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dialogTitle = useMemo(() => {
    if (isAddNewMode) return 'Add Manual Balance Entry';
    if (!selectedCell) return '';
    return dialogExistingBalance
      ? `Edit Manual Balance - ${selectedCell.name}`
      : `Add Manual Balance - ${selectedCell.name}`;
  }, [isAddNewMode, selectedCell?.name, dialogExistingBalance]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dialogButtonText = useMemo(() => {
    if (isAddNewMode) return 'Add';
    if (!selectedCell) return 'Add';
    return dialogExistingBalance ? 'Update' : 'Add';
  }, [isAddNewMode, dialogExistingBalance]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Balances Manual
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isSeanManualView && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadMenuOpen}
                  >
                    Export
                  </Button>
                  <Menu
                    anchorEl={downloadMenuAnchor}
                    open={Boolean(downloadMenuAnchor)}
                    onClose={handleDownloadMenuClose}
                  >
                    <MenuItem onClick={exportToCSV}>Download as CSV</MenuItem>
                    <MenuItem onClick={exportToPDF}>Download as PDF</MenuItem>
                  </Menu>
                </>
              )}
              {canManage && (
                isSeanManualView ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewEntry}
                  >
                    Add Entry
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddNewEntry}
                    >
                      Add Entry
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setBulkImportText('');
                        setBulkImportDialogOpen(true);
                      }}
                    >
                      Bulk Import
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setEntityFormData({
                          name: '',
                          category: 'business_bank_account',
                          account_number: '',
                          bank_name: '',
                          lender: '',
                          original_amount: '',
                          current_balance: '',
                          interest_rate: '',
                          monthly_payment: '',
                        });
                        setAddEntityDialogOpen(true);
                      }}
                    >
                      Add Account/Loan
                    </Button>
                  </>
                )
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {bulkImportLoading && bulkImportProgress && (
            <Alert 
              severity="info" 
              icon={<CircularProgress size={20} />}
              sx={{ mb: 2 }}
            >
              Importing... Row {bulkImportProgress.current} of {bulkImportProgress.total}
            </Alert>
          )}

          {/* Balance Overview Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}
          >
            <Table stickyHeader size="small" sx={{ 
              ...FIT_TABLE_SX,
              '& .MuiTableHead-root': {
                position: 'sticky',
                top: 0,
                zIndex: 100,
                backgroundColor: TABLE_HEADER_NEUTRAL_BG,
              },
              '& .MuiTableCell-head': {
                position: 'sticky',
                top: 0,
                zIndex: 100,
                overflow: 'hidden',
                isolation: 'isolate',
                height: 'auto',
                maxHeight: 'none',
                // Don't set backgroundColor here - let individual cells use their own solid fills
              },
              '& .MuiTableBody-root': {
                position: 'relative',
                zIndex: 1,
              },
              '& .MuiTableBody-root .MuiTableCell-root': {
                position: 'relative',
                zIndex: 1,
              },
              '& .MuiTableRow-root': {
                '&:first-of-type': {
                  '& .MuiTableCell-root': {
                    borderTop: 'none'
                  }
                }
              }
            }}>
              <FitColgroup
                cols={buildFitColgroup([
                  { key: 'date', pct: 9 },
                  ...accounts.map((account) => ({ key: `account-${account.id}` })),
                  ...loans
                    .filter((loan) => loan.loan_type === 'mortgage')
                    .map((loan) => ({ key: `mortgage-${loan.id}` })),
                  ...loans
                    .filter((loan) => loan.loan_type !== 'mortgage')
                    .map((loan) => ({ key: `loan-${loan.id}` })),
                  { key: 'net', pct: 8 },
                  { key: 'best-ever', pct: 7 },
                  { key: 'vs-last', pct: 7 },
                  { key: 'vs-best', pct: 7 },
                  ...(canManage ? [{ key: 'actions', pct: 7 }] : []),
                ])}
              />
              <TableHead sx={{ 
                position: 'sticky',
                top: 0,
                zIndex: 100,
                backgroundColor: TABLE_HEADER_NEUTRAL_BG,
                display: 'table-header-group'
              }}>
                <TableRow sx={{
                  backgroundColor: TABLE_HEADER_NEUTRAL_BG,
                  '& .MuiTableCell-root': {
                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                    position: 'sticky',
                    top: 0,
                    verticalAlign: 'top',
                  }
                }}>
                  <TableCell align="center" sx={{ 
                    backgroundColor: '#343A40 !important', // Dark grey background (#343A40)
                    color: '#ffffff', // White text
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    minHeight: '60px',
                    padding: '12px 8px'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, textAlign: 'center' }}>
                      <strong>Date</strong>
                      <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', fontWeight: 'normal' }}>
                        (Year - Week No.)
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* Account columns (bank accounts) */}
                  {accounts.map((account) => (
                    <TableCell 
                      key={`account-${account.id}`} 
                      align="center"
                      sx={{ 
                        backgroundColor: '#E3F2FD !important', // Light blue - bank accounts
                        color: '#000000', // Black text
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        overflow: 'hidden',
                        minHeight: '60px',
                        padding: '12px 8px'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                        <AccountIcon fontSize="small" sx={{ color: '#343A40' }} />
                        {formatHeaderLabel(account.account_name, 'account').map((line, i) => (
                          <strong key={i}>{line}</strong>
                        ))}
                        <Typography component="span" variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(0,0,0,0.55)' }}>
                          ID {account.id}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                  {/* Mortgage columns (rendered first) */}
                  {loans.filter(loan => loan.loan_type === 'mortgage').map((loan) => {
                    const icon = <MortgageIcon fontSize="small" />;
                    
                    // Clean up the loan name by removing redundant prefixes
                    let cleanName = loan.loan_name;
                    if (cleanName.includes('Mortgage with ')) {
                      cleanName = cleanName.replace('Mortgage with ', '');
                    }
                    
                    return (
                      <TableCell 
                        key={`loan-${loan.id}`} 
                        align="center"
                        sx={{ 
                          backgroundColor: '#E8F5E9 !important', // Light green - mortgages
                          color: '#000000', // Black text
                          position: 'sticky',
                          top: 0,
                          zIndex: 100,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          overflow: 'hidden',
                          minHeight: '60px',
                          padding: '12px 8px'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                          {React.cloneElement(icon, { sx: { color: '#343A40', fontSize: 'small' } })}
                          {formatHeaderLabel(cleanName, 'mortgage').map((line, i) => (
                            <strong key={i}>{line}</strong>
                          ))}
                        </Box>
                      </TableCell>
                    );
                  })}
                  {/* Loan columns (rendered after mortgages) */}
                  {loans.filter(loan => loan.loan_type !== 'mortgage').map((loan) => {
                    const icon = <LoanIcon fontSize="small" />;
                    
                    // Clean up the loan name by removing redundant prefixes
                    let cleanName = loan.loan_name;
                    if (cleanName.includes('Loan from ')) {
                      cleanName = cleanName.replace('Loan from ', '');
                    }
                    
                    return (
                      <TableCell 
                        key={`loan-${loan.id}`} 
                        align="center"
                        sx={{ 
                          backgroundColor: '#FFF3E0 !important', // Light amber - personal loans
                          color: '#000000', // Black text
                          position: 'sticky',
                          top: 0,
                          zIndex: 100,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          overflow: 'hidden',
                          minHeight: '60px',
                          padding: '12px 8px'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                          {React.cloneElement(icon, { sx: { color: '#343A40', fontSize: 'small' } })}
                          {formatHeaderLabel(cleanName, 'loan').map((line, i) => (
                            <strong key={i} style={{ display: 'block', whiteSpace: 'nowrap' }}>{line}</strong>
                          ))}
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ 
                    backgroundColor: '#007BFF !important', // Bright blue background (#007BFF)
                    color: '#ffffff', // White text
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    minHeight: '60px',
                    padding: '12px 8px'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <strong>Net Position</strong>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: `${TABLE_HEADER_NEUTRAL_BG} !important`,
                    color: '#000000', // Black text
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    minHeight: '60px',
                    padding: '12px 8px'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <strong>Best Ever</strong>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: `${TABLE_HEADER_NEUTRAL_BG} !important`,
                    color: '#000000', // Black text
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    minHeight: '60px',
                    padding: '12px 8px'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      {formatHeaderLabel('Vs last Entry', 'vs').map((line, i) => (
                        <strong key={i}>{line}</strong>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: `${TABLE_HEADER_NEUTRAL_BG} !important`,
                    color: '#000000', // Black text
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    minHeight: '60px',
                    padding: '12px 8px'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      {formatHeaderLabel('vs Best Ever', 'vs').map((line, i) => (
                        <strong key={i}>{line}</strong>
                      ))}
                    </Box>
                  </TableCell>
                  {canManage && (
                    <TableCell align="center" sx={{ 
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      backgroundColor: `${TABLE_HEADER_NEUTRAL_BG} !important`,
                      color: '#000000', // Black text
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      minHeight: '60px',
                      padding: '12px 8px'
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <strong>Actions</strong>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWeekRanges.map((weekRange, index) => (
                    <TableRow 
                      key={`${weekRange.startStr}-${weekRange.endStr}`} 
                      hover
                      sx={{
                        '& .MuiTableCell-root': {
                          position: 'relative',
                          zIndex: 1,
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                            {weekRange.yearWeek}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: '0.7rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {weekRange.dateRange}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Account balance cells */}
                      {accounts.map((account) => {
                        const balance = getCachedBalanceForWeek('account', account.id, weekRange.startStr, weekRange.endStr);
                        // Accounts are positive by default
                        const balanceValue = balance ? Math.abs(balance.balance) : 0;
                        const eurEquivalent = balance && balance.currency === 'GBP' 
                          ? convertToEUR(Math.abs(balance.balance), balance.currency) 
                          : null;
                        return (
                          <TableCell 
                            key={`account-${account.id}-${weekRange.startStr}`} 
                            align="right"
                            onClick={() => handleCellClick('account', account.id, weekRange.startStr, weekRange.endStr, account.account_name)}
                            sx={{ 
                              cursor: canManage ? 'pointer' : 'default',
                              '&:hover': canManage ? { backgroundColor: 'action.hover' } : {}
                            }}
                          >
                            {balance ? (
                              <Tooltip 
                                title={`Entered: ${formatDateTime(balance.date_entered)}${balance.updated_at !== balance.created_at ? ` | Updated: ${formatDateTime(balance.updated_at)}` : ''}${balance.notes ? ` | Notes: ${balance.notes}` : ''}`}
                                arrow
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium"
                                      color="success.main"
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      {formatCurrency(balanceValue, balance.currency)}
                                    </Typography>
                                    {eurEquivalent !== null && (
                                      <Typography 
                                        variant="caption" 
                                        color="success.main"
                                        sx={{ fontSize: '0.7rem', opacity: 0.7, whiteSpace: 'nowrap' }}
                                      >
                                        {formatCurrency(eurEquivalent, 'EUR')}
                                      </Typography>
                                    )}
                                  </Box>
                                  {balance.notes && (
                                    <NoteIcon 
                                      sx={{ 
                                        fontSize: '0.9rem', 
                                        color: 'text.secondary',
                                        opacity: 0.7
                                      }} 
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      {/* Mortgage balance cells (rendered first) */}
                      {loans.filter(loan => loan.loan_type === 'mortgage').map((loan) => {
                        // HTB Mortgage uses direct entry only (no mirroring)
                        let balance: ManualBalance | null = null;
                        const isHTBMortgage = loan.loan_name.includes('HTB Mortgage');
                        
                        if (isHTBMortgage) {
                          balance = getHTBMortgageBalance(weekRange.startStr, weekRange.endStr);
                        } else {
                          balance = getCachedBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
                        }
                        
                        // Mortgages are negative by default - negate the balance for display
                        const balanceValue = balance ? -Math.abs(balance.balance) : 0;
                        
                        // For HTB Mortgage, always use GBP currency and show EUR equivalent
                        const currency = isHTBMortgage ? 'GBP' : (balance?.currency || 'EUR');
                        
                        // Show EUR equivalent only if there's a balance and currency is GBP
                        const eurEquivalent = balance && currency === 'GBP' 
                          ? convertToEUR(-Math.abs(balance.balance), currency) 
                          : null;
                        return (
                          <TableCell 
                            key={`loan-${loan.id}-${weekRange.startStr}`} 
                            align="right"
                            onClick={() => handleCellClick('loan', loan.id, weekRange.startStr, weekRange.endStr, loan.loan_name)}
                            sx={{ 
                              cursor: canManage ? 'pointer' : 'default',
                              '&:hover': canManage ? { backgroundColor: 'action.hover' } : {}
                            }}
                          >
                            {balance ? (
                              <Tooltip 
                                title={`Entered: ${formatDateTime(balance.date_entered)}${balance.updated_at !== balance.created_at ? ` | Updated: ${formatDateTime(balance.updated_at)}` : ''}${balance.notes ? ` | Notes: ${balance.notes}` : ''}`}
                                arrow
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium"
                                      color="error.main"
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      {formatCurrency(balanceValue, currency)}
                                    </Typography>
                                    {eurEquivalent !== null && (
                                      <Typography 
                                        variant="caption" 
                                        color="error.main"
                                        sx={{ fontSize: '0.7rem', opacity: 0.7, whiteSpace: 'nowrap' }}
                                      >
                                        {formatCurrency(eurEquivalent, 'EUR')}
                                      </Typography>
                                    )}
                                  </Box>
                                  {balance.notes && (
                                    <NoteIcon 
                                      sx={{ 
                                        fontSize: '0.9rem', 
                                        color: 'text.secondary',
                                        opacity: 0.7
                                      }} 
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      {/* Loan balance cells (rendered after mortgages) */}
                      {loans.filter(loan => loan.loan_type !== 'mortgage').map((loan) => {
                        const balance = getCachedBalanceForWeek('loan', loan.id, weekRange.startStr, weekRange.endStr);
                        
                        // Loans are negative by default - negate the balance for display
                        const balanceValue = balance ? -Math.abs(balance.balance) : 0;
                        const currency = balance?.currency || 'EUR';
                        const eurEquivalent = balance && currency === 'GBP' ? convertToEUR(-Math.abs(balance.balance), currency) : null;
                        
                        return (
                          <TableCell 
                            key={`loan-${loan.id}-${weekRange.startStr}`} 
                            align="right"
                            onClick={() => handleCellClick('loan', loan.id, weekRange.startStr, weekRange.endStr, loan.loan_name)}
                            sx={{ 
                              cursor: canManage ? 'pointer' : 'default',
                              '&:hover': canManage ? { backgroundColor: 'action.hover' } : {}
                            }}
                          >
                            {balance ? (
                              <Tooltip 
                                title={`Entered: ${formatDateTime(balance.date_entered)}${balance.updated_at !== balance.created_at ? ` | Updated: ${formatDateTime(balance.updated_at)}` : ''}${balance.notes ? ` | Notes: ${balance.notes}` : ''}`}
                                arrow
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium"
                                      color="error.main"
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      {formatCurrency(balanceValue, currency)}
                                    </Typography>
                                    {eurEquivalent !== null && (
                                      <Typography 
                                        variant="caption" 
                                        color="error.main"
                                        sx={{ fontSize: '0.7rem', opacity: 0.7, whiteSpace: 'nowrap' }}
                                      >
                                        {formatCurrency(eurEquivalent, 'EUR')}
                                      </Typography>
                                    )}
                                  </Box>
                                  {balance.notes && (
                                    <NoteIcon 
                                      sx={{ 
                                        fontSize: '0.9rem', 
                                        color: 'text.secondary',
                                        opacity: 0.7
                                      }} 
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      {/* Net position cell */}
                      <TableCell 
                        align="right"
                        sx={{ 
                          fontWeight: 'bold'
                        }}
                      >
                        {(() => {
                          const netPosition = getNetPosition(weekRange.startStr, weekRange.endStr);
                          return (
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={netPosition >= 0 ? 'success.main' : 'error.main'}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {formatCurrency(netPosition, 'EUR')}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      {/* Best Ever column */}
                      <TableCell align="center">
                        {isBestEverWeek(weekRange.startStr, weekRange.endStr) && (
                          <Tooltip title="Best Ever Net Position" arrow>
                            <BestEverIcon 
                              sx={{ 
                                fontSize: '1.5rem', 
                                color: 'warning.main'
                              }} 
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      {/* vs Last Entry comparison cell */}
                      <TableCell align="center">
                        {(() => {
                          const prevWeek = index + 1 < filteredWeekRanges.length ? filteredWeekRanges[index + 1] : null;
                          const difference = getVsLastEntryDifference(weekRange.startStr, weekRange.endStr, prevWeek);
                          if (difference === null) {
                            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                          }
                          return (
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={difference > 0 ? 'success.main' : difference < 0 ? 'error.main' : 'text.secondary'}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {difference > 0 ? '+' : ''}{formatCurrency(difference, 'EUR')}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      {/* vs Best Ever comparison cell */}
                      <TableCell align="center">
                        {(() => {
                          const difference = getBestEverDifference(weekRange.startStr, weekRange.endStr);
                          if (difference === null) {
                            // This is the best ever week
                            return (
                              <Typography 
                                variant="body2" 
                                fontWeight="bold"
                                color="warning.main"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Best Ever
                              </Typography>
                            );
                          }
                          return (
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={difference > 0 ? 'success.main' : difference < 0 ? 'error.main' : 'text.secondary'}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {difference > 0 ? '+' : ''}{formatCurrency(difference, 'EUR')}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      {/* Delete All button column */}
                      {canManage && (
                        <TableCell align="center" className="fit-table-no-clip">
                          <Button
                            variant="text"
                            color="error"
                            size="small"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete EVERY balance in this row?\n\nThis will delete all balances for the week ${weekRange.yearWeek} (${weekRange.dateRange}).\n\nThis action cannot be undone.`)) {
                                handleDeleteAllBalancesForWeek(weekRange.startStr, weekRange.endStr);
                              }
                            }}
                            sx={{ 
                              minWidth: 'auto', 
                              padding: '4px',
                              border: 'none',
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Date"
                type="date"
                value={formData.date_entered}
                onChange={(e) => setFormData({ ...formData, date_entered: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              <TextField
                label="Time"
                type="time"
                value={formData.time_entered}
                onChange={(e) => setFormData({ ...formData, time_entered: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                inputProps={{ step: 60 }} // 1 minute steps
              />
            </Box>
            {isAddNewMode ? (
              <TextField
                select
                label="Account/Loan"
                value={formData.account_id ? `account-${formData.account_id}` : formData.loan_id ? `loan-${formData.loan_id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.startsWith('account-')) {
                    const accountId = parseInt(value.replace('account-', ''));
                    const account = accounts.find(a => a.id === accountId);
                    setFormData({
                      ...formData,
                      account_id: accountId,
                      loan_id: null,
                      currency: account ? getCurrencyForAccountOrLoan(account.account_name) : 'EUR'
                    });
                  } else if (value.startsWith('loan-')) {
                    const loanId = parseInt(value.replace('loan-', ''));
                    const loan = loans.find(l => l.id === loanId);
                    setFormData({
                      ...formData,
                      account_id: null,
                      loan_id: loanId,
                      currency: loan ? getCurrencyForAccountOrLoan(loan.loan_name) : 'EUR'
                    });
                  } else {
                    setFormData({
                      ...formData,
                      account_id: null,
                      loan_id: null
                    });
                  }
                }}
                fullWidth
                required
              >
                <MenuItem value="">Select Account or Loan</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={`account-${account.id}`} value={`account-${account.id}`}>
                    {accountExportHeader(account)}
                  </MenuItem>
                ))}
                {loans.map((loan) => (
                  <MenuItem key={`loan-${loan.id}`} value={`loan-${loan.id}`}>
                    {loan.loan_name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Account/Loan"
                value={selectedCell?.name || ''}
                disabled
                fullWidth
              />
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Balance"
                type="text"
                value={formData.balance}
                onChange={(e) => {
                  // Remove all non-digit characters except decimal point
                  // Also remove currency symbols that might be typed
                  let value = e.target.value.replace(/[^\d.]/g, '');
                  
                  // Ensure only one decimal point
                  const parts = value.split('.');
                  if (parts.length > 2) {
                    value = parts[0] + '.' + parts.slice(1).join('');
                  }
                  
                  // Limit to 2 decimal places
                  if (parts.length === 2 && parts[1].length > 2) {
                    value = parts[0] + '.' + parts[1].substring(0, 2);
                  }
                  
                  // Check if user is typing a decimal (ends with . or has . followed by digits)
                  const hasDecimalPoint = value.includes('.');
                  const endsWithDecimal = value.endsWith('.');
                  
                  // Format with commas for thousands, but preserve decimal point and decimal digits while typing
                  if (value && value !== '.') {
                    // Remove commas for parsing
                    const cleanValue = value.replace(/,/g, '');
                    
                    if (hasDecimalPoint) {
                      // Split into integer and decimal parts
                      const [intPart, decPart] = cleanValue.split('.');
                      
                      // Format integer part with commas
                      if (intPart) {
                        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        // Preserve decimal point and decimal digits
                        setFormData({ ...formData, balance: formattedInt + '.' + (decPart || '') });
                      } else {
                        // Just decimal point, no integer part yet
                        setFormData({ ...formData, balance: '0.' + (decPart || '') });
                      }
                    } else {
                      // No decimal point - format integer part with commas
                      const numValue = parseFloat(cleanValue);
                      if (!isNaN(numValue)) {
                        const formatted = numValue.toLocaleString('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        });
                        setFormData({ ...formData, balance: formatted });
                      } else {
                        setFormData({ ...formData, balance: value });
                      }
                    }
                  } else {
                    // Empty or just decimal point
                    setFormData({ ...formData, balance: value });
                  }
                }}
                fullWidth
                required
                autoFocus
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.currency === 'EUR' ? '€' : formData.currency === 'GBP' ? '£' : formData.currency === 'USD' ? '$' : formData.currency}
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (isAddNewMode) {
                    // In add new mode, use account_id or loan_id from formData
                    if (formData.account_id) {
                      const latestBalance = getLatestBalance('account', formData.account_id);
                      if (latestBalance) {
                        const formattedBalance = latestBalance.balance.toLocaleString('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2
                        });
                        setFormData({ ...formData, balance: formattedBalance });
                      } else {
                        showToast('No previous balance found for this account');
                      }
                    } else if (formData.loan_id) {
                      const latestBalance = getLatestBalance('loan', formData.loan_id);
                      if (latestBalance) {
                        const formattedBalance = latestBalance.balance.toLocaleString('en-US', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2
                        });
                        setFormData({ ...formData, balance: formattedBalance });
                      } else {
                        showToast('No previous balance found for this loan');
                      }
                    } else {
                      showToast('Please select an account or loan first');
                    }
                  } else {
                    // In cell click mode, use selectedCell
                    if (!selectedCell) return;
                    const latestBalance = getLatestBalance(selectedCell.type, selectedCell.id);
                    if (latestBalance) {
                      // Format the balance with commas
                      const formattedBalance = latestBalance.balance.toLocaleString('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                      });
                      setFormData({ ...formData, balance: formattedBalance });
                    } else {
                      showToast('No previous balance found for this account/loan');
                    }
                  }
                }}
                disabled={isAddNewMode && !formData.account_id && !formData.loan_id}
                sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
              >
                Copy Latest
              </Button>
            </Box>
            <TextField
              select
              label="Currency"
              value={formData.currency}
              disabled={!isAddNewMode}
              fullWidth
              helperText={
                isAddNewMode 
                  ? 'Currency will be set based on selected account/loan'
                  : selectedCell 
                    ? `Currency locked to ${formData.currency} for ${selectedCell.name}` 
                    : ''
              }
            >
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
            </TextField>
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            {dialogExistingBalance && (
              <>
                <TextField
                  label="Created At"
                  value={formatDateTime(dialogExistingBalance.created_at)}
                  disabled
                  fullWidth
                  size="small"
                />
                {dialogExistingBalance.updated_at !== dialogExistingBalance.created_at && (
                  <TextField
                    label="Last Updated"
                    value={formatDateTime(dialogExistingBalance.updated_at)}
                    disabled
                    fullWidth
                    size="small"
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogExistingBalance && (
            <Button 
              onClick={async () => {
                if (!dialogExistingBalance) return;
                
                if (window.confirm('Are you sure you want to delete this manual balance entry?')) {
                  try {
                    // Store original balance for potential revert
                    const balanceToDelete = dialogExistingBalance;
                    const originalBalances = [...balances];
                    
                    // Optimistically remove from state immediately
                    setBalances(prev => prev.filter(b => b.id !== balanceToDelete.id));
                    
                    // Close modal immediately
                    handleCloseDialog();
                    
                    // Show toast immediately
                    const entityName = balanceToDelete.account_name || balanceToDelete.loan_name || 'Unknown';
                    const balanceDisplay = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: balanceToDelete.currency || 'EUR',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(Math.abs(balanceToDelete.balance));
                    showToast(`Deleting balance ${balanceDisplay} from ${entityName}...`);
                    
                    // Make API call asynchronously in background
                    (async () => {
                      try {
                        const response: any = await api.delete(`/api/manual-balances/${balanceToDelete.id}`, {
                          timeout: 15000 // 15 seconds timeout
                        });
                        
                        if (response.data.success) {
                          showToast('Manual balance deleted successfully');
                          
                          // Refresh only balances in background - non-blocking
                          setTimeout(async () => {
                            try {
                              const balancesRes = await api.get('/api/manual-balances/', { timeout: 10000 });
                              setBalances(balancesRes.data.balances || []);
                            } catch (refreshErr) {
                              console.error('Background refresh failed (non-critical):', refreshErr);
                            }
                          }, 500); // Small delay to let the server process
                        } else {
                          // Revert optimistic update on failure
                          setBalances(originalBalances);
                          setError(response.data.message || 'Failed to delete manual balance');
                          showToast(response.data.message || 'Failed to delete manual balance');
                        }
                      } catch (err: any) {
                        console.error('Error deleting manual balance:', err);
                        
                        // Revert optimistic update on error
                        setBalances(originalBalances);
                        
                        const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete manual balance';
                        setError(errorMsg);
                        
                        // Check if it's a timeout but the request might have succeeded
                        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                          showToast(`Deletion may have succeeded (timeout occurred). Please refresh to verify.`);
                        } else {
                          showToast(errorMsg);
                        }
                      }
                    })();
                  } catch (err: any) {
                    console.error('Error in delete handler:', err);
                    setError(err.message || 'Failed to delete manual balance');
                  }
                }
              }} 
              variant="outlined" 
              color="error"
            >
              Delete
            </Button>
          )}
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            type="button"
          >
            {dialogButtonText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Account/Loan Dialog */}
      <Dialog open={addEntityDialogOpen} onClose={handleCloseAddEntityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add New Account/Loan
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={entityFormData.name}
              onChange={(e) => setEntityFormData({ ...entityFormData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., AIB 096, HTB Mortgage, Personal Loan"
            />
            <TextField
              select
              label="Category"
              value={entityFormData.category}
              onChange={(e) => setEntityFormData({ ...entityFormData, category: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="mortgage">Mortgage</MenuItem>
              <MenuItem value="business_loan">Business Loan</MenuItem>
              <MenuItem value="personal_loan">Personal Loan</MenuItem>
              <MenuItem value="business_bank_account">Business Bank Account</MenuItem>
              <MenuItem value="personal_bank_account">Personal Bank Account</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            {/* Show account-specific fields for bank accounts and other */}
            {(entityFormData.category === 'business_bank_account' || 
              entityFormData.category === 'personal_bank_account' || 
              entityFormData.category === 'other') && (
              <>
                <TextField
                  label="Account Number"
                  value={entityFormData.account_number}
                  onChange={(e) => setEntityFormData({ ...entityFormData, account_number: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label="Bank Name"
                  value={entityFormData.bank_name}
                  onChange={(e) => setEntityFormData({ ...entityFormData, bank_name: e.target.value })}
                  fullWidth
                  placeholder="e.g., AIB, BOI, Revolut"
                />
              </>
            )}

            {/* Show loan-specific fields for loans and mortgages */}
            {(entityFormData.category === 'mortgage' || 
              entityFormData.category === 'business_loan' || 
              entityFormData.category === 'personal_loan') && (
              <>
                <TextField
                  label="Lender"
                  value={entityFormData.lender}
                  onChange={(e) => setEntityFormData({ ...entityFormData, lender: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., AIB, BOI, Bank of Ireland"
                />
                <TextField
                  label="Original Amount"
                  type="number"
                  value={entityFormData.original_amount}
                  onChange={(e) => setEntityFormData({ ...entityFormData, original_amount: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
                <TextField
                  label="Current Balance"
                  type="number"
                  value={entityFormData.current_balance}
                  onChange={(e) => setEntityFormData({ ...entityFormData, current_balance: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
                <TextField
                  label="Interest Rate (%)"
                  type="number"
                  value={entityFormData.interest_rate}
                  onChange={(e) => setEntityFormData({ ...entityFormData, interest_rate: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
                <TextField
                  label="Monthly Payment"
                  type="number"
                  value={entityFormData.monthly_payment}
                  onChange={(e) => setEntityFormData({ ...entityFormData, monthly_payment: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddEntityDialog}>Cancel</Button>
          <Button onClick={handleAddEntity} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog 
        open={bulkImportDialogOpen} 
        onClose={() => {
          if (!bulkImportLoading) {
            setBulkImportDialogOpen(false);
          }
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Bulk Import Balances
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')} sx={{ whiteSpace: 'pre-wrap' }}>
                {error}
              </Alert>
            )}
            {bulkImportLoading && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                {bulkImportProgress 
                  ? `Importing... Row ${bulkImportProgress.current} of ${bulkImportProgress.total}`
                  : 'Preparing import...'}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              Paste data with Date, Rev Biz Ire, and BOI Mortgage columns.
              <br />
              <strong>Format:</strong> Date (e.g., "Wednesday, 5 February 2025") | Rev Biz Ire (e.g., "€155,829.30") | BOI Mortgage (e.g., "-€423,635.31")
              <br />
              <strong>Separator:</strong> Tab-separated or space-separated (headers optional)
              <br />
              <strong>Example:</strong>
              <pre style={{ margin: '8px 0', fontSize: '0.875rem' }}>
{`Wednesday, 5 February 2025	€155,829.30	-€423,635.31
Friday, 18 April 2025	€178,664.19	-€415,805.72
Friday, 4 July 2025	€234,261.19	-€401,476.46`}
              </pre>
            </Alert>
            <TextField
              label="Data"
              value={bulkImportText}
              onChange={(e) => setBulkImportText(e.target.value)}
              fullWidth
              multiline
              rows={15}
              disabled={bulkImportLoading}
              placeholder={`Date	Rev Biz Ire	BOI Mortgage
Sunday, 28 July 2019	€34,944.22	-€700,561.80
Sunday, 13 December 2020	€3,431.16	-€652,057.06`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (!bulkImportLoading) {
                setBulkImportDialogOpen(false);
                setBulkImportText('');
                setError('');
              }
            }}
            disabled={bulkImportLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkImport} 
            variant="contained" 
            color="primary"
            disabled={bulkImportLoading}
            startIcon={bulkImportLoading ? <CircularProgress size={16} /> : null}
          >
            {bulkImportLoading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={APP_TOP_TOAST_SNACKBAR_SX}
      >
        <Alert onClose={handleCloseToast} severity={toast?.severity || 'info'} sx={{ width: '100%' }}>
          <Stack spacing={0.25}>
            <Typography variant="body2">{toast?.message}</Typography>
            <ToastCodeCaption
              code={toast?.diagnostics.code || ''}
              requestId={toast?.diagnostics.requestId}
              jobId={toast?.diagnostics.jobId}
              errorCode={toast?.diagnostics.errorCode}
              copyBody={toast?.message}
            />
          </Stack>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManualBalanceManagement;
